/**
 * POS-inspector order-centric detail view (feat-115) — the five ADR §D7
 * sections, extracted from PosInspector.tsx so they render free of page chrome
 * (no ScorecardNavigation / contexts) and are unit-testable + restyleable in
 * isolation.
 *
 * Visual layout follows design-lead's spec-time mock (design/design-notes.md +
 * pos-inspector-detail mock), 3-layer system, SEMANTIC TOKENS ONLY (no raw
 * green / red / slate palette). Key rules from the mock:
 *  - A non-converting attempt is CORRECT, never `danger`/red — neutral `preview`
 *    box + backend `non_conversion_reason` + "a clean miss — not a fault".
 *  - Converted = `success`; converted_via item(s) in `gold` (ties to the
 *    highlighted line item). Subset header "N attempts → M converted".
 *  - Timeline: wider context band with the matcher window flagged (coral accent);
 *    context-only rows dimmed + tagged, with a "matcher window opens" marker.
 *  - Canceled order = `warning` amber (legitimate state, not red); superseded
 *    snapshot = `preview` note.
 *
 * Data flow + linkage semantics are ADR-locked; design-lead does a fidelity pass
 * at PR. Two presentation mappings are flagged for backend confirmation below
 * (quality_level scale, speaker→role int convention).
 */
import React from 'react';
import {
  PosOrderInspectDetail,
  TimelineEntry,
  InteractionTimelineEntry,
  Conversion,
  CoveringShift,
} from '../../../services/posInspectorService';

// ---------- shared formatters ----------

export const money = (n: number, currency = 'USD'): string =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency }).format(n);

/** Render a shift-relative offset (seconds) as a signed m:ss relative to the order. */
export function relTime(seconds: number): string {
  const sign = seconds < 0 ? '−' : '+';
  const abs = Math.abs(Math.round(seconds));
  const m = Math.floor(abs / 60);
  const s = abs % 60;
  return `${sign}${m}:${String(s).padStart(2, '0')}`;
}

// L&L criterion vocabulary (CLAUDE.md / design-notes §4) — emoji only for known
// keys, silent fallback otherwise so non-L&L tenants render cleanly.
const CRITERION_EMOJI: Record<string, string> = {
  metric_1: '🥩',
  metric_2: '🍖',
  metric_3: '🍫',
  drink_promotion: '🥤',
};
const criterionEmoji = (key: string): string => CRITERION_EMOJI[key] ?? '';

// quality_level is the upsell "n level" — integer 1–3, nullable (architect:
// services/prompt_config.py + Grove template). Mock renders "L{n} · {phrase}".
const QUALITY_LABEL: Record<number, string> = { 1: 'Basic ask', 2: 'Specific item', 3: 'Descriptive flourish' };
const qualityLabel = (q: number | null): string | null => (q == null ? null : `L${q} · ${QUALITY_LABEL[q] ?? `level ${q}`}`);

/** Per-entry server label — ADR §D4.1: name from server_name, fall back to id/"Unknown". */
const serverLabel = (e: { server_name: string | null; server_id: string }): string =>
  e.server_name ?? e.server_id ?? 'Unknown';

/** Initials for an avatar (e.g. "Jane K." → "JK"). */
export const serverInitials = (label: string): string =>
  label
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('') || '?';

/**
 * POSITIONAL per-server color (design-lead TOKENS.md §1j / feat-115): a server's
 * index within the order's covering-shift set → series-N (1..5). Name + initials
 * stay the authoritative signal; series-* only reinforces. Class strings are STATIC
 * literals so Tailwind's content scanner emits them — dynamic `bg-series-${n}`
 * would be purged.
 */
export const buildSeriesByServerId = (coveringShifts: CoveringShift[]): Map<string, number> =>
  new Map(coveringShifts.map((s, i) => [s.server_id, (i % 5) + 1]));

interface SeriesClasses {
  avatar: string;
  rowBorder: string; // server-speech / covering-shift row accent
  rowBorderSoft: string; // customer mic-indicator (faint)
  name: string;
}
const SERIES: Record<number, SeriesClasses> = {
  1: { avatar: 'bg-series-1 text-series-1-on', rowBorder: 'border-series-1 bg-series-1-light/60', rowBorderSoft: 'border-series-1/30', name: 'text-series-1' },
  2: { avatar: 'bg-series-2 text-series-2-on', rowBorder: 'border-series-2 bg-series-2-light/60', rowBorderSoft: 'border-series-2/30', name: 'text-series-2' },
  3: { avatar: 'bg-series-3 text-series-3-on', rowBorder: 'border-series-3 bg-series-3-light/60', rowBorderSoft: 'border-series-3/30', name: 'text-series-3' },
  4: { avatar: 'bg-series-4 text-series-4-on', rowBorder: 'border-series-4 bg-series-4-light/60', rowBorderSoft: 'border-series-4/30', name: 'text-series-4' },
  5: { avatar: 'bg-series-5 text-series-5-on', rowBorder: 'border-series-5 bg-series-5-light/60', rowBorderSoft: 'border-series-5/30', name: 'text-series-5' },
};
export const seriesClasses = (n: number): SeriesClasses => SERIES[n] ?? SERIES[1];

/** Initials avatar + tinted name for a server (positional series-N) — design-notes §"v2 heart". */
const ServerBadge: React.FC<{ name: string; series: number }> = ({ name, series }) => {
  const s = seriesClasses(series);
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`w-6 h-6 rounded-full text-[10px] font-bold inline-flex items-center justify-center shrink-0 ${s.avatar}`}>
        {serverInitials(name)}
      </span>
      <span className={`text-sm font-semibold ${s.name}`}>{name}</span>
    </span>
  );
};

// ---------- chips ----------

export const Chip: React.FC<{ tone?: 'neutral' | 'success' | 'preview' | 'warning' | 'gold' | 'coral'; children: React.ReactNode; className?: string }> = ({
  tone = 'neutral',
  children,
  className = '',
}) => {
  const tones: Record<string, string> = {
    neutral: 'bg-cream-dark text-inkMuted border-softBorder',
    success: 'bg-success-light text-success border-success/30',
    preview: 'bg-preview-light text-preview border-preview-border',
    warning: 'bg-warning-surface text-warning-on border-warning-on/30',
    gold: 'bg-gold-light text-gold border-gold/30',
    coral: 'bg-coral-light text-coral border-coral/30',
  };
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded border text-xs font-medium ${tones[tone]} ${className}`}>
      {children}
    </span>
  );
};

/** Order state chip — canceled is a legitimate state (warning amber), not a fault (design-notes §3). */
export const StateChip: React.FC<{ state: string }> = ({ state }) => {
  const s = state.toLowerCase();
  const canceled = s === 'canceled' || s === 'cancelled' || s === 'voided';
  return <Chip tone={canceled ? 'warning' : 'neutral'}>{state}</Chip>;
};

const CapsLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="text-xs font-semibold uppercase tracking-wide text-inkMuted">{children}</div>
);

// ---------- section chrome (3-layer card) ----------

const Section: React.FC<{ title: string; testId: string; subtitle?: React.ReactNode; right?: React.ReactNode; children: React.ReactNode }> = ({
  title,
  testId,
  subtitle,
  right,
  children,
}) => (
  <section className="bg-white border border-softBorder rounded-2xl shadow-sm overflow-hidden mb-4" data-testid={testId}>
    <div className="px-5 py-4 border-b border-softBorder bg-cream/40 flex items-start justify-between gap-3">
      <div>
        <h2 className="text-lg font-display font-semibold text-ink">{title}</h2>
        {subtitle && <div className="text-xs text-inkMuted mt-0.5">{subtitle}</div>}
      </div>
      {right}
    </div>
    <div className="px-5 py-4">{children}</div>
  </section>
);

const Field: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  // min-w-0 lets the grid cell shrink (grid items default to min-width:auto), and
  // break-words wraps long values inside the cell instead of overflowing into the next
  // column (feat-122: the long Source/Order id was overlapping Opened (local)).
  <div className="min-w-0">
    <CapsLabel>{label}</CapsLabel>
    <div className="text-ink mt-0.5 break-words">{children}</div>
  </div>
);

// ---------- detail ----------

export const OrderDetail: React.FC<{ detail: PosOrderInspectDetail }> = ({ detail }) => {
  const { order, snapshots_summary, covering_shifts, no_covering_shift_reason, timeline, line_items, associations, conversions, legacy_item_links } = detail;

  // Line items satisfying a conversion (converted_via values, flattened across categories) → gold highlight (design-notes §5.2).
  const convertedItemNames = new Set<string>(
    conversions.filter((c) => c.converted && c.converted_via).flatMap((c) => Object.values(c.converted_via!).flat()),
  );
  // Interactions whose attempt converted → green "Converted" pill on the timeline row (feat-120),
  // so the conversion is visible right in the timeline, not only in the §5 match analysis.
  const convertedInteractionIds = new Set<string>(
    conversions.filter((c) => c.converted).map((c) => c.shift_interaction_id),
  );
  const multiShift = covering_shifts.length > 1;
  // Positional per-server series: server index in the covering-shift set → series-N.
  const seriesByServerId = buildSeriesByServerId(covering_shifts);
  const seriesOf = (serverId: string): number => seriesByServerId.get(serverId) ?? 1;
  // shift_interaction_id → attributing server {name, series} for the §5 attempt blocks,
  // keyed off the timeline interaction entries the attempts link to.
  const attemptServer = new Map<string, { name: string; series: number }>(
    timeline.entries
      .filter((e): e is InteractionTimelineEntry => e.kind === 'interaction')
      .map((e) => [e.id, { name: serverLabel(e), series: seriesOf(e.server_id) }]),
  );

  return (
    <div data-testid="pos-inspector-detail">
      {/* 1 — Order header */}
      <Section title="Order" testId="pos-inspector-section-order" right={<StateChip state={order.state} />}>
        {!snapshots_summary.is_current && (
          <div className="mb-3 p-2 rounded bg-preview-light text-preview text-xs border border-preview-border" data-testid="pos-inspector-superseded-note">
            Viewing a superseded snapshot (v{order.snapshot_version} of {snapshots_summary.total_snapshots}; current is v{snapshots_summary.current_version}).
          </div>
        )}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 text-sm">
          <Field label="Source / Order"><span className="font-mono tabular-nums text-xs break-all">{order.source}/{order.vendor_order_id}</span></Field>
          <Field label="Opened (local)"><span className="tabular-nums">{order.opened_at_local}</span> <span className="text-inkMuted">({order.timezone})</span></Field>
          <Field label="Opened (UTC)"><span className="font-mono tabular-nums text-xs">{order.opened_at_utc}</span></Field>
          <Field label="Location">{order.location_name}</Field>
          <Field label="Final total"><span className="tabular-nums">{money(order.final_total, order.currency)}</span></Field>
          <Field label="Net sales"><span className="tabular-nums">{money(order.net_sales, order.currency)}</span></Field>
          <Field label="Snapshot">
            <span className="tabular-nums">v{order.snapshot_version}</span>
            {snapshots_summary.total_snapshots > 1 && (
              <Chip tone="preview" className="ml-2">{snapshots_summary.total_snapshots - 1} earlier superseded</Chip>
            )}
          </Field>
        </div>
      </Section>

      {/* 2 — Covering shift(s) / no-shift reason (AC5) */}
      <Section title="Covering shift(s)" testId="pos-inspector-section-shifts">
        {covering_shifts.length === 0 ? (
          <div className="p-3 bg-cream-dark border border-softBorder rounded-lg text-sm text-inkMuted" data-testid="pos-inspector-no-shift">
            {no_covering_shift_reason ?? 'No covering shift.'}
          </div>
        ) : (
          <ul className="text-sm space-y-2">
            {covering_shifts.map((s) => (
              <li
                key={s.shift_id}
                className={`flex flex-wrap items-center gap-x-4 gap-y-1 border-l-4 rounded-r pl-3 py-2 ${seriesClasses(seriesOf(s.server_id)).rowBorder}`}
              >
                <ServerBadge name={s.server_name ?? s.server_id} series={seriesOf(s.server_id)} />
                <span className="text-inkMuted tabular-nums">shift {s.shift_start} → {s.shift_end}</span>
                <Chip tone="neutral">order at +{Math.round(s.shift_offset_seconds)}s into shift</Chip>
              </li>
            ))}
          </ul>
        )}
      </Section>

      {/* 3 — Lead-up timeline */}
      <Section
        title="Lead-up timeline"
        testId="pos-inspector-section-timeline"
        subtitle={
          <>
            Context band {relTime(-timeline.context_window.pre_seconds)} … {relTime(timeline.context_window.post_seconds)}; matcher window{' '}
            {relTime(-timeline.matcher_window.pre_seconds)} … {relTime(timeline.matcher_window.post_seconds)} flagged.
          </>
        }
      >
        {timeline.entries.length === 0 ? (
          <p className="text-sm text-inkMuted">No timeline — requires a covering shift to place the order in shift-relative time.</p>
        ) : (
          <>
            <Timeline entries={timeline.entries} matcherPre={timeline.matcher_window.pre_seconds} seriesByServerId={seriesByServerId} convertedInteractionIds={convertedInteractionIds} />
            <p className="text-[11px] text-inkMuted mt-3 italic">
              Speaker attribution is mic/shift-based{multiShift ? ' (this window spans multiple covering shifts)' : ''} — a line may reflect an
              adjacent server’s mic bleed. Best-effort, shown as-is.
            </p>
          </>
        )}
      </Section>

      {/* 4 — Line items + classification */}
      <Section title="Line items" testId="pos-inspector-section-items" subtitle="Gold = satisfied an upsell criterion (converted_via).">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-inkMuted border-b border-softBorder">
              <th className="py-1.5 pr-3 font-semibold">Item</th>
              <th className="py-1.5 pr-3 text-right font-semibold">Qty</th>
              <th className="py-1.5 pr-3 text-right font-semibold">Total</th>
              <th className="py-1.5 font-semibold">Classification</th>
            </tr>
          </thead>
          <tbody>
            {line_items.map((li) => {
              const highlighted = convertedItemNames.has(li.name);
              return (
                <tr key={li.id} className={`border-b border-softBorder/60 ${highlighted ? 'bg-gold-light' : ''}`} data-testid={highlighted ? 'pos-inspector-item-converted' : undefined}>
                  <td className="py-1.5 pr-3">{li.name}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{li.quantity}</td>
                  <td className="py-1.5 pr-3 text-right tabular-nums">{money(li.total_money, order.currency)}</td>
                  <td className="py-1.5">
                    {li.classification ? (
                      <span className="flex flex-wrap gap-1 items-center">
                        {li.classification.categories.map((c) => (
                          <Chip key={c} tone="neutral">{c}</Chip>
                        ))}
                        {li.classification.is_featured_drink && <Chip tone="gold">featured drink</Chip>}
                      </span>
                    ) : (
                      <span className="text-xs text-inkMuted">unclassified</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Section>

      {/* 5 — Match analysis (the heart) */}
      <MatchAnalysis associations={associations} conversions={conversions} legacyItemLinks={legacy_item_links} attemptServer={attemptServer} />
    </div>
  );
};

// ---------- timeline ----------

const OrderOpenedMarker: React.FC = () => (
  // coral is reserved for the order-opened (ring-up, 0s) marker + the matcher-window divider.
  <li className="flex items-center gap-2 py-1" data-testid="pos-inspector-order-opened">
    <span className="font-mono tabular-nums text-xs font-semibold text-coral w-12 shrink-0 text-right">0s</span>
    <span className="inline-flex items-center text-[10px] font-semibold uppercase tracking-wide text-coral bg-coral-light rounded-full px-3 py-1 whitespace-nowrap">
      order opened
    </span>
    <span className="h-px flex-1 bg-coral/40" />
  </li>
);

const Timeline: React.FC<{ entries: TimelineEntry[]; matcherPre: number; seriesByServerId: Map<string, number>; convertedInteractionIds: Set<string> }> = ({ entries, matcherPre, seriesByServerId, convertedInteractionIds }) => {
  const firstInWindowIdx = entries.findIndex((e) => e.in_matcher_window);
  const firstAfterOrderIdx = entries.findIndex((e) => e.time_vs_order_seconds >= 0);
  return (
    <ul className="space-y-1">
      {entries.map((e, i) => (
        <React.Fragment key={`${e.kind}:${e.id}`}>
          {i === firstInWindowIdx && firstInWindowIdx > 0 && (
            <li className="flex items-center gap-2 py-1" aria-hidden data-testid="pos-inspector-matcher-marker">
              <span className="h-px flex-1 bg-coral/40" />
              <span className="text-[10px] font-semibold uppercase tracking-wide text-coral whitespace-nowrap">
                {relTime(-matcherPre)} · matcher window opens
              </span>
              <span className="h-px flex-1 bg-coral/40" />
            </li>
          )}
          {i === firstAfterOrderIdx && <OrderOpenedMarker />}
          <TimelineRow entry={e} series={seriesByServerId.get(e.server_id) ?? 1} converted={e.kind === 'interaction' && convertedInteractionIds.has(e.id)} />
        </React.Fragment>
      ))}
      {/* All lead-up entries precede the order → ring-up marker closes the stream. */}
      {firstAfterOrderIdx === -1 && <OrderOpenedMarker />}
    </ul>
  );
};

const TimelineRow: React.FC<{ entry: TimelineEntry; series: number; converted: boolean }> = ({ entry, series, converted }) => {
  const inWin = entry.in_matcher_window;
  const isCustomer = entry.kind === 'transcript_part' && entry.speaker !== 0;
  const s = seriesClasses(series);
  // Left-accent is the per-server series color (matcher window shown via the divider
  // marker + opacity dimming, NOT the border). Customer lines get the faint mic-owner
  // series border + neutral surface (design-notes §"v2 heart").
  const border = isCustomer ? `border-l-4 ${s.rowBorderSoft} bg-cream-dark/40` : `border-l-4 ${s.rowBorder}`;
  return (
    <li
      className={`text-sm pl-3 py-1.5 rounded-r ${border} ${inWin ? '' : 'opacity-60'}`}
      id={entry.kind === 'interaction' ? `timeline-int-${entry.id}` : undefined}
    >
      <div className="flex items-baseline gap-2">
        <span className="font-mono tabular-nums text-xs text-inkMuted w-12 shrink-0">{relTime(entry.time_vs_order_seconds)}</span>
        <div className="min-w-0 flex-1">
          {entry.kind === 'transcript_part' ? (
            <TranscriptLine entry={entry} inWin={inWin} />
          ) : (
            <InteractionLine entry={entry} series={series} converted={converted} />
          )}
        </div>
      </div>
    </li>
  );
};

const TranscriptLine: React.FC<{ entry: Extract<TimelineEntry, { kind: 'transcript_part' }>; inWin: boolean }> = ({ entry, inWin }) => {
  // Show the RAW speaker number ([0] = server turn, [1] = customer turn) rather than
  // resolving it to a server name / "Customer". The speaker→name mapping is mic-based and
  // unreliable (crosstalk / adjacent-mic bleed — see the note under the timeline), so the
  // raw speaker index is the honest diagnostic signal. (Server-action attribution still
  // appears on interaction rows, which are tied to a shift, not to a transcript speaker.)
  return (
    <div className="flex items-start gap-2">
      <span className="font-mono text-xs font-semibold text-inkMuted shrink-0 mt-0.5">[{entry.speaker}]</span>
      <div className="min-w-0">
        <span className={inWin ? 'text-ink' : 'text-inkMuted'}>{entry.text}</span>
        {!inWin && <Chip tone="neutral" className="ml-2">context only</Chip>}
      </div>
    </div>
  );
};

const InteractionLine: React.FC<{ entry: InteractionTimelineEntry; series: number; converted: boolean }> = ({ entry, series, converted }) => (
  <div>
    <div className="mb-0.5"><ServerBadge name={serverLabel(entry)} series={series} /></div>
    <span className="flex flex-wrap items-center gap-1.5">
      <Chip tone={entry.is_upsell_attempt ? 'coral' : 'neutral'}>{entry.interaction_type}</Chip>
      {converted && <Chip tone="success">✓ Converted</Chip>}
      {qualityLabel(entry.quality_level) && <Chip tone="neutral">{qualityLabel(entry.quality_level)}</Chip>}
      {entry.service_criteria.map((sc) => (
        // Bind both backend flags (ADR §D7 D6-refinement): fulfilled drives the
        // tone (success vs neutral "not met" — never red, it's diagnostic), and
        // on/off-scorecard is a suffix marker.
        <Chip key={sc.criterion_key} tone={sc.fulfilled ? 'success' : 'neutral'}>
          {criterionEmoji(sc.criterion_key) && <span>{criterionEmoji(sc.criterion_key)}</span>}
          {sc.criterion_key}
          <span>{sc.fulfilled ? '✓' : '· not met'}</span>
          <span className="opacity-70">{sc.on_scorecard ? '· on-scorecard' : '· off-scorecard'}</span>
        </Chip>
      ))}
      {!entry.in_matcher_window && <Chip tone="neutral">context only</Chip>}
    </span>
    <div className="text-inkMuted text-xs mt-0.5 italic">“{entry.transcript_text}”</div>
  </div>
);

// ---------- match analysis (the heart, §5) ----------

const MatchAnalysis: React.FC<{ associations: PosOrderInspectDetail['associations']; conversions: Conversion[]; legacyItemLinks: PosOrderInspectDetail['legacy_item_links']; attemptServer: Map<string, { name: string; series: number }> }> = ({ associations, conversions, legacyItemLinks, attemptServer }) => {
  const convertedCount = conversions.filter((c) => c.converted).length;
  const total = conversions.length;
  const ratio = total > 0 ? Math.round((convertedCount / total) * 100) : 0;

  const showLegacy = legacyItemLinks.length > 0 && conversions.length === 0;

  return (
    <Section
      title="Match analysis"
      testId="pos-inspector-section-match"
      subtitle={
        showLegacy
          ? 'Revel item-grain attribution.'
          : `${associations.length} attempt(s) → ${convertedCount} converted. Conversions are a subset of associations.`
      }
      right={
        !showLegacy && total > 0 ? (
          <div className="w-32" aria-label={`${convertedCount} of ${total} converted`}>
            <div className="h-2 rounded-full bg-cream-dark overflow-hidden">
              <div className="h-full bg-success" style={{ width: `${ratio}%` }} />
            </div>
            <div className="text-[10px] text-inkMuted mt-1 text-right tabular-nums">{convertedCount}/{total} converted</div>
          </div>
        ) : undefined
      }
    >
      {showLegacy ? (
        <LegacyLinks links={legacyItemLinks} />
      ) : conversions.length === 0 ? (
        <p className="text-sm text-inkMuted">No upsell attempts associated with this order.</p>
      ) : (
        <div className="space-y-3">
          {conversions.map((c, i) => (
            <ConversionCard key={`${c.shift_interaction_id}:${c.criterion_key}:${i}`} c={c} interactionType={associations.find((a) => a.shift_interaction_id === c.shift_interaction_id)?.interaction_type} server={attemptServer.get(c.shift_interaction_id)} />
          ))}
        </div>
      )}
    </Section>
  );
};

/** 3-column linkage card: Interaction → Criterion → Outcome (design-notes §5.4). */
const ConversionCard: React.FC<{ c: Conversion; interactionType?: string; server?: { name: string; series: number } }> = ({ c, interactionType, server }) => (
  <div className="border border-softBorder rounded-xl p-3 grid grid-cols-1 md:grid-cols-[1fr_auto_1fr_auto_1.2fr] items-center gap-3" data-testid="pos-inspector-conversion-card">
    {/* 1 — Interaction */}
    <div>
      <CapsLabel>Interaction</CapsLabel>
      {server && <div className="mb-0.5"><ServerBadge name={server.name} series={server.series} /></div>}
      {interactionType && <div className="text-sm text-ink">{interactionType}</div>}
      <a href={`#timeline-int-${c.shift_interaction_id}`} className="text-xs text-coral hover:underline">Jump to timeline →</a>
      <div className="font-mono text-[10px] text-inkMuted truncate">{c.shift_interaction_id}</div>
    </div>
    <Arrow />
    {/* 2 — Criterion */}
    <div>
      <CapsLabel>Criterion</CapsLabel>
      <div className="text-sm text-ink flex items-center gap-1">
        {criterionEmoji(c.criterion_key) && <span>{criterionEmoji(c.criterion_key)}</span>}
        {c.criterion_key}
      </div>
      <div className="flex flex-wrap gap-1 mt-1">
        {c.target_categories.map((t) => (
          <Chip key={t} tone="neutral">{t}</Chip>
        ))}
      </div>
    </div>
    <Arrow />
    {/* 3 — Outcome */}
    <div>
      <CapsLabel>Outcome</CapsLabel>
      {c.converted && c.converted_via ? (
        <div>
          <Chip tone="success">Converted</Chip>
          {Object.entries(c.converted_via).map(([bucket, items]) => (
            <div key={bucket} className="mt-1">
              <div className="text-xs text-success">Matched on {bucket}</div>
              <div className="flex flex-wrap gap-1 mt-0.5">
                {(items ?? []).map((n) => (
                  <Chip key={n} tone="gold">{n}</Chip>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="rounded-lg bg-preview-light border border-preview-border p-2">
          <Chip tone="preview">Not converted</Chip>
          {c.non_conversion_reason && <div className="text-xs text-preview mt-1">{c.non_conversion_reason}</div>}
          <div className="text-[11px] text-inkMuted mt-1 italic">A clean miss — not a fault.</div>
        </div>
      )}
    </div>
  </div>
);

const Arrow: React.FC = () => <div className="hidden md:block text-inkMuted text-lg select-none" aria-hidden>→</div>;

const LegacyLinks: React.FC<{ links: PosOrderInspectDetail['legacy_item_links'] }> = ({ links }) => (
  <table className="w-full text-sm" data-testid="pos-inspector-legacy-links">
    <thead>
      <tr className="text-left text-inkMuted border-b border-softBorder">
        <th className="py-1.5 pr-3 font-semibold">Item</th>
        <th className="py-1.5 pr-3 font-semibold">Match rule</th>
        <th className="py-1.5 pr-3 font-semibold">Δt</th>
        <th className="py-1.5 font-semibold">Explanation</th>
      </tr>
    </thead>
    <tbody>
      {links.map((l, i) => (
        <tr key={`${l.shift_interaction_id}:${i}`} className="border-b border-softBorder/60">
          <td className="py-1.5 pr-3"><Chip tone="gold">{l.item_name}</Chip></td>
          <td className="py-1.5 pr-3">{l.match_rule}</td>
          <td className="py-1.5 pr-3 font-mono tabular-nums text-xs">{relTime(l.time_diff_seconds)}</td>
          <td className="py-1.5 text-xs text-inkMuted">{l.explanation ?? '—'}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

// Re-export type used above for clarity.
export type { CoveringShift };
